/**
 * API client for HMVM AI Code Reviewer backend.
 * 
 * Phase 00: Basic health check endpoints only.
 * Phase 03: Intake endpoints.
 * Phase 04: Preflight endpoints.
 * Phase 19: Enhanced Frontend UI endpoints.
 */

import { KnowledgeControlAuthError, loadKnowledgeAdminToken } from './knowledgeControlAuth'
import { redirectToReviewAuth } from './hmvmAuth'

export function resolveApiBaseUrl(
  viteUrl: string | undefined,
  _isProd: boolean,
): string {
  if (viteUrl !== undefined && viteUrl !== '') {
    return viteUrl.replace(/\/$/, '')
  }
  // Same-origin in both Vite and production so the frontend proxy / nginx
  // forwards /api and /health. Avoids CORS between 127.0.0.1 and localhost.
  return ''
}

const API_BASE_URL = resolveApiBaseUrl(
  import.meta.env.VITE_API_BASE_URL,
  import.meta.env.PROD,
)



async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, { ...init, credentials: 'include' })
  const url = String(input)
  if (
    import.meta.env.PROD &&
    response.status === 401 &&
    !url.includes('/api/v1/session') &&
    !url.includes('/health')
  ) {
    redirectToReviewAuth()
  }
  return response
}

export interface HealthResponse {
  status: 'ok' | 'degraded' | 'down'
  version: string
  environment: string
  checks: {
    database: string
    redis: string
  }
  timestamp: string
}

export interface ReadinessResponse {
  ready: boolean
  checks: {
    [key: string]: string  // Component status: 'ok', 'not_implemented', etc.
  }
}

export interface DetectedTechnology {
  name: string
  category: string
  version?: string
  evidence_paths: string[]
  confidence?: string
}

export interface DeclaredCommand {
  name: string
  command: string
  source_file: string
  is_risky: boolean
  risk_reason?: string
}

export interface SecretIndicator {
  type: string
  file_path: string
  line_number?: number
  detector_id: string
  severity: string
  redacted_preview: string
}

export interface RiskyArtifact {
  type: string
  file_path: string
  risk_level: string
  reason: string
  metadata: Record<string, unknown>
}

export interface ScanStatistics {
  total_files: number
  scanned_files: number
  skipped_files: number
  truncated_files: number
  total_bytes_scanned: number
  scan_duration_seconds: number
}

export interface PreflightDecision {
  safe_to_proceed: boolean
  reasons: string[]
  blockers: string[]
}

export interface PreflightResponse {
  preflight_id: string
  source_id: string
  project_id: string
  status: 'pending' | 'running' | 'succeeded' | 'succeeded_with_warnings' | 'blocked' | 'failed'
  scanner_version: string
  detected_technologies: DetectedTechnology[]
  declared_commands: DeclaredCommand[]
  secret_indicators: SecretIndicator[]
  risky_artifacts: RiskyArtifact[]
  scan_statistics?: ScanStatistics
  decision?: PreflightDecision
  error_message?: string
  created_at: string
  completed_at?: string
}

export interface TriggerPreflightRequest {
  source_id: string
  force_rerun?: boolean
}

// Phase 19: Enhanced Frontend UI Types

export interface AuditSummaryStats {
  total_projects?: number
  total_audits: number
  total_findings: number
  critical_findings: number
  high_findings: number
  medium_findings: number
  low_findings: number
  info_findings: number
  completed_audits: number
  running_audits: number
  failed_audits: number
}

export interface RecentAudit {
  id: string
  project_id: string
  project_name: string
  status: string
  total_findings: number
  critical_count: number
  high_count: number
  medium_count: number
  low_count: number
  info_count: number
  started_at: string | null
  completed_at: string | null
  created_at: string
}

export interface TaskResponse {
  id: string
  audit_run_id: string
  track: string
  status: string
  findings_count: number
  started_at: string | null
  completed_at: string | null
  error_message: string | null
}

export interface WorkflowEventResponse {
  id: string
  audit_run_id: string
  event_kind: string
  previous_state: string | null
  new_state: string
  transition_reason: string | null
  created_at: string
}

export interface AuditDetailsResponse {
  id: string
  project_id: string
  project_name: string
  status: string
  requested_tracks: string[]
  tracks_executed: string[]
  total_findings: number
  critical_count: number
  high_count: number
  medium_count: number
  low_count: number
  info_count: number
  risk_score: number | null
  debt_score: number | null
  compliance_score: number | null
  quick_scan: boolean
  include_ai_analysis: boolean
  max_findings_per_track: number
  timeout_seconds: number | null
  started_at: string | null
  completed_at: string | null
  error_message: string | null
  created_at: string
  selected_pack_ids?: Array<Record<string, unknown>> | null
  tasks: TaskResponse[]
  workflow_events: WorkflowEventResponse[]
}

export interface FindingListItem {
  id: string
  audit_run_id: string
  project_id: string
  project_name: string
  title: string
  severity: string
  category: string
  track: string
  status: string
  confidence: string
  primary_location: string | null
  line_number: number | null
  tags: string[]
  created_at: string
  updated_at: string
}

export interface FindingsResponse {
  findings: FindingListItem[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface EvidenceResponse {
  id: string
  evidence_type: string
  description: string
  content_preview: string | null
  storage_ref: string | null
  created_at: string
  line_number?: number | null
  snippet_start_line?: number | null
  snippet_anchor_line?: number | null
  source?: string | null
  reconstructed?: boolean
}

export interface AuditRunContext {
  id: string
  status: string
  project_id: string
  project_name: string
  requested_tracks: string[]
  tracks_executed: string[]
  quick_scan: boolean
  started_at: string | null
  completed_at: string | null
  created_at: string
}

export interface FindingDetailsResponse {
  id: string
  audit_run_id: string
  finding_fingerprint: string
  track: string
  severity: string
  category: string
  confidence: string
  status: string
  title: string
  description: string
  impact: string | null
  primary_location: string | null
  line_number: number | null
  reproduction_steps: string[]
  reproduction_script: string | null
  remediation_guidance: string | null
  remediation_effort: string | null
  cwe_ids: string[]
  cve_ids: string[]
  tags: string[]
  extra_metadata: Record<string, unknown>
  evidence: EvidenceResponse[]
  audit_context: AuditRunContext | null
  created_at: string
  updated_at: string
  first_seen_at: string
  last_seen_at: string
}

export interface RelatedFindingItem {
  id: string
  audit_run_id: string
  project_name: string
  title: string
  severity: string
  status: string
  relation_type: 'same_fingerprint' | 'same_file' | 'same_rule'
  primary_location: string | null
  created_at: string
}

export interface RelatedFindingsResponse {
  related: RelatedFindingItem[]
  total: number
}

export interface FindingsFilters {
  audit_run_id?: string
  project_id?: string
  severity?: string
  category?: string
  track?: string
  status?: string
  confidence?: string
  search?: string
  sort?: string
  order?: string
  page?: number
  page_size?: number
}

// Phase 4: Projects & Audit Runs Types

export interface ProjectListItem {
  id: string
  display_name: string
  source_type: string
  external_id: string | null
  description: string | null
  audit_count: number
  last_audit_at: string | null
  created_at: string
  updated_at: string
  latest_audit_id: string | null
  latest_audit_status: string | null
  latest_audit_critical_count: number
  latest_audit_high_count: number
  latest_audit_total_findings: number
}

export interface ProjectsResponse {
  projects: ProjectListItem[]
  total: number
}

export interface AuditRunListItem {
  id: string
  project_id: string
  project_name: string
  status: string
  requested_tracks: string[]
  tracks_executed: string[]
  total_findings: number
  critical_count: number
  high_count: number
  medium_count: number
  low_count: number
  info_count: number
  risk_score: number | null
  debt_score: number | null
  compliance_score: number | null
  quick_scan: boolean
  started_at: string | null
  completed_at: string | null
  error_message: string | null
  created_at: string
  duration_seconds: number | null
}

export interface AuditRunsResponse {
  audits: AuditRunListItem[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface AuditRunsFilters {
  project_id?: string
  status?: string
  page?: number
  page_size?: number
}

export interface StartAuditRequest {
  project_id: string
  requested_tracks?: string[]
  quick_scan?: boolean
  include_ai_analysis?: boolean
  selected_packs?: Array<{ pack_id: string; version?: string }>
  timeout_seconds?: number
}

export interface StartAuditResponse {
  id: string
  project_id: string
  project_name: string
  status: string
  requested_tracks: string[]
  selected_pack_ids?: Array<Record<string, unknown>> | null
  worker_enqueued: boolean
  created_at: string
}

export interface EvidenceListItem {
  id: string
  evidence_type: string
  description: string
  content_preview: string | null
  file_path: string | null
  line_number: number | null
  finding_id: string
  finding_title: string
  finding_severity: string
  audit_run_id: string
  project_id: string
  project_name: string
  created_at: string
}

export interface EvidenceListResponse {
  items: EvidenceListItem[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface RuntimeResponse {
  version: string
  app_name: string
  environment: string
  orchestrator_configured: boolean
  knowledge_admin_configured: boolean
  available_report_formats: string[]
  available_audit_tracks: string[]
}

export interface AuditTaskProgress {
  task_id: string
  task_type: string
  track: string
  status: string
  findings_count: number
  started_at: string | null
  completed_at: string | null
  error_message: string | null
}

export interface AuditProgressResponse {
  audit_run_id: string
  status: string
  total_tasks: number
  completed_tasks: number
  running_tasks: number
  failed_tasks: number
  pending_tasks: number
  total_findings: number
  started_at: string | null
  estimated_completion_at: string | null
  progress_percent: number
  tasks?: AuditTaskProgress[]
}

export interface ReauditListItem {
  reaudit_id: string
  status: string
  new_audit_id: string
  original_audit_id?: string | null
  comparison_ref: string
  net_improvement_score: number | null
  created_at: string
  completed_at: string | null
}

export interface ReauditListResponse {
  reaudits: ReauditListItem[]
  total: number
  limit: number
  offset: number
}

export interface ReauditTriggerResponse {
  reaudit_run_id: string
  new_audit_run_id: string
  original_audit_run_id: string
  fix_run_id: string
  comparison_ref: string
  status: string
  created_at: string
}

export interface RecentFixRunItem {
  fix_run_id: string
  audit_run_id: string
  status: string
  target_branch: string
  base_ref: string
  created_at: string
  completed_at: string | null
  fixes_applied: number
  fixes_failed: number
  error_message: string | null
}

export interface IntakeResponse {
  status: string
  project_id: string | null
  source_id: string | null
  error_message: string | null
  duration_seconds: number
}

async function readApiError(response: Response, fallback: string): Promise<string> {
  const data = await response.json().catch(() => ({ detail: fallback })) as { detail?: unknown }
  const detail = data.detail
  if (typeof detail === 'string') return detail
  if (detail && typeof detail === 'object' && detail !== null && 'message' in detail) {
    return String((detail as { message: unknown }).message)
  }
  return fallback
}

// Phase 7: Scoring & Reports Types

export interface SeverityDistribution {
  critical: number
  high: number
  medium: number
  low: number
  info: number
  total: number
}

export interface ScoreBreakdown {
  severity_weighted_sum: number
  confidence_adjustment: number
  normalization_factor: number
  capped_value: number
  contributing_tracks: string[]
  explanation: string
}

export interface ScoringResult {
  risk_score: number | null
  debt_score: number | null
  compliance_score: number | null
  risk_breakdown: ScoreBreakdown | null
  debt_breakdown: ScoreBreakdown | null
  compliance_breakdown: ScoreBreakdown | null
  severity_distribution: SeverityDistribution
  tracks_executed: string[]
  tracks_requested: string[]
  track_coverage_ratio: number
  total_findings: number
  audit_run_id: string
  scored_at: string
}

export type ReportFormat = 'html' | 'pdf' | 'json'
export type ReportStatus = 'pending' | 'generating' | 'completed' | 'failed'

export interface ReportGenerationRequest {
  format: ReportFormat
  locale?: 'ru' | 'en'
}

export interface ReportGenerationResponse {
  report_id: string
  audit_run_id: string
  format: ReportFormat
  status: ReportStatus
  created_at: string
}

export interface ReportStatusResponse {
  report_id: string
  audit_run_id: string
  project_name?: string | null
  format: ReportFormat
  status: ReportStatus
  created_at: string
  generated_at: string | null
  file_size_bytes: number | null
  error_message: string | null
}

// Phase 8: Fix with AI types
export type FixRunStatus = 'pending' | 'running' | 'completed' | 'partial' | 'failed' | 'cancelled' | 'timeout'
export type FixStrategy = 'conservative' | 'balanced' | 'aggressive'

export interface FixRunCreateRequest {
  finding_ids: string[]
  target_branch: string
  base_ref: string
  auto_commit?: boolean
  fix_strategy?: FixStrategy
  max_fixes?: number
  timeout_seconds?: number
}

export interface FixRunCreateResponse {
  fix_run_id: string
  audit_run_id: string
  target_branch: string
  status: FixRunStatus
  created_at: string
}

export interface FixRunStatusResponse {
  fix_run_id: string
  audit_run_id: string
  status: FixRunStatus
  target_branch: string
  base_ref: string
  created_at: string
  started_at: string | null
  completed_at: string | null
  base_commit: string | null
  final_commit: string | null
  commits_created: string[]
  fixes_applied: number
  fixes_failed: number
  fixed_finding_ids: string[]
  failed_finding_ids: string[]
  error_message: string | null
  continuation_metadata: Record<string, unknown>
}

// Phase 9: FixRun History & Batch Operations types
export interface FixRunListItem {
  fix_run_id: string
  audit_run_id: string
  status: FixRunStatus
  target_branch: string
  base_ref: string
  created_at: string
  completed_at: string | null
  fixes_applied: number
  fixes_failed: number
  error_message: string | null
  cancellable: boolean
}

export interface FixRunListResponse {
  items: FixRunListItem[]
  pagination: {
    total: number
    limit: number
    has_more: boolean
    next_cursor: string | null
  }
}

export interface BatchCancelRequest {
  fix_run_ids: string[]
  audit_run_id: string
}

export interface BatchCancelResponse {
  cancelled: string[]
  skipped: Array<{ fix_run_id: string; reason: string }>
  errors: Array<{ fix_run_id: string; message: string }>
}

// Phase 10: Knowledge Base types
export interface KBEntryListItem {
  id: string
  rule_id: string
  version: string
  title: string
  category: string
  severity: string
  confidence: string
  format_type: string
  tags: string[]
  has_remediation: boolean
}

export interface KBEntryDetail {
  id: string
  rule_id: string
  version: string
  title: string
  description: string
  category: string
  severity: string
  confidence: string
  format_type: string
  impact: string | null
  remediation: string | null
  references: string[]
  cwe_ids: string[]
  tags: string[]
  provenance_source: string
}

export interface KBListResponse {
  items: KBEntryListItem[]
  total_count: number
  page: number
  page_size: number
  total_pages: number
}

export interface KBStatsResponse {
  total_entries: number
  by_category: Record<string, number>
  by_severity: Record<string, number>
  by_format: Record<string, number>
}

export type KnowledgeLifecycleStatus =
  | 'draft'
  | 'testing'
  | 'approved'
  | 'active'
  | 'deprecated'
  | 'rejected'

export type KnowledgeValidationStatus = 'pending' | 'pass' | 'fail' | 'skip'
export type KnowledgeApprovalAction = 'approve' | 'reject' | 'revise' | 'activate'
export type KnowledgeControlAction =
  | 'validate'
  | 'submit_testing'
  | 'approve'
  | 'reject'
  | 'revise'
  | 'activate'
  | 'deprecate'

export interface KnowledgeControlListItem {
  id: string
  rule_id: string
  version: string
  status: KnowledgeLifecycleStatus
  format_type: string
  category: string
  severity: string
  title: string
  validation_status: KnowledgeValidationStatus
  version_number: number
  proposal_source: string | null
  created_at: string
  activated_at: string | null
  legal_actions: KnowledgeControlAction[]
}

export interface KnowledgeControlListResponse {
  items: KnowledgeControlListItem[]
  total_count: number
  page: number
  page_size: number
  total_pages: number
}

export interface KnowledgeControlDetail {
  id: string
  rule_id: string
  version: string
  status: KnowledgeLifecycleStatus
  format_type: string
  category: string
  severity: string
  confidence: string
  title: string
  description: string
  impact: string | null
  match_spec: Record<string, unknown>
  message: string
  remediation: string | null
  references: string[] | null
  cwe_ids: string[] | null
  tags: string[] | null
  provenance_source: string
  provenance_author: string | null
  proposal_source: string | null
  proposal_rationale: string | null
  approved_at: string | null
  approval_notes: string | null
  activated_at: string | null
  deprecated_at: string | null
  deprecated_reason: string | null
  validation_status: KnowledgeValidationStatus
  validation_results: Record<string, unknown> | null
  rule_hash: string
  version_number: number
  enabled: boolean
  legal_actions: KnowledgeControlAction[]
  created_at: string
  updated_at: string | null
}

export interface KnowledgeControlHistoryEntry {
  id: string
  rule_id: string
  from_status: KnowledgeLifecycleStatus | null
  to_status: KnowledgeLifecycleStatus
  actor_user_id: string | null
  actor_name: string | null
  actor_role: string | null
  action: string | null
  reason: string
  notes: string | null
  created_at: string
}

export interface KnowledgeControlHistoryResponse {
  rule_id: string
  rule_identifier: string
  entries: KnowledgeControlHistoryEntry[]
  total_count: number
}

export interface KnowledgeControlStats {
  total_rules: number
  rules_by_status: Record<string, number>
  rules_by_format: Record<string, number>
  rules_by_severity: Record<string, number>
  pending_proposals: number
  pending_approvals: number
  approved_ready: number
  active_packs: number
  total_packs: number
}

export interface KnowledgeValidationResultDetail {
  validation_type: string
  status: KnowledgeValidationStatus
  details: Record<string, unknown> | null
  error_message: string | null
  passed_fixtures: string[] | null
  failed_fixtures: string[] | null
}

export interface KnowledgeValidationResponse {
  rule_id: string
  rule_identifier: string
  overall_status: KnowledgeValidationStatus
  results: KnowledgeValidationResultDetail[]
  can_activate: boolean
  validated_at: string
  message: string
}

export interface KnowledgeApprovalResponse {
  rule_id: string
  rule_identifier: string
  action: KnowledgeApprovalAction
  new_status: KnowledgeLifecycleStatus
  approved_by: string | null
  approved_at: string
  message: string
}

export interface KnowledgeTransitionResponse {
  rule_id: string
  rule_identifier: string
  from_status: KnowledgeLifecycleStatus | null
  to_status: KnowledgeLifecycleStatus
  actor: string | null
  actor_role: string | null
  idempotent: boolean
  legal_actions: KnowledgeControlAction[]
  message: string
}

export type KnowledgePackAction =
  | KnowledgeControlAction
  | 'edit'
  | 'add_member'
  | 'remove_member'
  | 'create_version'

export interface KnowledgePackListItem {
  id: string
  pack_id: string
  version: string
  name: string
  status: KnowledgeLifecycleStatus
  format_type: string
  provenance_source: string
  validation_status: KnowledgeValidationStatus
  version_number: number
  member_count: number
  pack_hash: string
  created_at: string
  activated_at: string | null
  legal_actions: KnowledgePackAction[]
  tags?: string[]
}

export interface KnowledgePackListResponse {
  items: KnowledgePackListItem[]
  total_count: number
  page: number
  page_size: number
  total_pages: number
}

export interface KnowledgePackMember {
  id: string
  rule_id: string
  version: string
  status: KnowledgeLifecycleStatus
  title: string
  format_type: string
  severity: string
  validation_status: KnowledgeValidationStatus
  enabled: boolean
  order_index: number
  active: boolean
}

export interface KnowledgePackDetail {
  id: string
  pack_id: string
  version: string
  name: string
  description: string | null
  format_type: string
  provenance_source: string
  tags: string[] | null
  status: KnowledgeLifecycleStatus
  validation_status: KnowledgeValidationStatus
  validation_results: Record<string, unknown> | null
  version_number: number
  enabled: boolean
  pack_hash: string
  published_at: string | null
  approved_at: string | null
  activated_at: string | null
  deprecated_at: string | null
  deprecated_reason: string | null
  membership_mutable: boolean
  legal_actions: KnowledgePackAction[]
  members: KnowledgePackMember[]
  created_at: string
  updated_at: string | null
}

export interface KnowledgePackHistoryEntry {
  id: string
  pack_id: string
  from_status: KnowledgeLifecycleStatus | null
  to_status: KnowledgeLifecycleStatus
  actor_user_id: string | null
  actor_name: string | null
  actor_role: string | null
  action: string | null
  reason: string
  notes: string | null
  created_at: string
}

export interface KnowledgePackHistoryResponse {
  pack_id: string
  pack_identifier: string
  entries: KnowledgePackHistoryEntry[]
  total_count: number
}

export interface KnowledgePackValidationResponse {
  pack_id: string
  pack_identifier: string
  overall_status: KnowledgeValidationStatus
  results: KnowledgeValidationResultDetail[]
  can_activate: boolean
  member_count: number
  non_active_members: number
  validated_at: string
  message: string
}

export interface KnowledgePackTransitionResponse {
  pack_id: string
  pack_identifier: string
  from_status: KnowledgeLifecycleStatus | null
  to_status: KnowledgeLifecycleStatus
  actor: string | null
  actor_role: string | null
  idempotent: boolean
  legal_actions: KnowledgePackAction[]
  message: string
}

export interface KBPackListItem {
  id: string
  pack_id: string
  version: string
  name: string
  description: string | null
  format_type: string
  provenance_source: string
  tags: string[]
  member_count: number
}

export interface KBPackListResponse {
  items: KBPackListItem[]
  total_count: number
  page: number
  page_size: number
  total_pages: number
}

export interface KnowledgePackResolveResponse {
  mode: 'legacy_all_active_rules' | 'selected_packs'
  packs: Array<Record<string, unknown>>
  rules: Array<{
    id: string
    rule_id: string
    version: string
    rule_hash: string
    format_type: string
    title: string
    pack_id?: string | null
    pack_version?: string | null
  }>
  total_packs: number
  total_rules: number
  ruleset_hash: string
  skipped_non_active_members: number
  snapshot_id: string | null
  message: string
}

export type AuthVia = 'sso' | 'tg'

export interface MeResponse {
  ok: boolean
  sub: string
  name: string
  role: string
  auth_via?: string
}

export const api = {
  async createSession(accessToken: string): Promise<MeResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: accessToken }),
    })
    if (!response.ok) {
      throw new Error(`Session failed: ${response.statusText}`)
    }
    return response.json()
  },

  async logout(): Promise<void> {
    await apiFetch(`${API_BASE_URL}/api/v1/session`, { method: 'DELETE' })
  },

  async me(): Promise<MeResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/me`)
    if (!response.ok) {
      throw new Error(`Me failed: ${response.statusText}`)
    }
    return response.json()
  },

  async health(): Promise<HealthResponse> {
    const response = await apiFetch(`${API_BASE_URL}/health`)
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.statusText}`)
    }
    return response.json()
  },

  async readiness(): Promise<ReadinessResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/readyz`)
    if (!response.ok) {
      throw new Error(`Readiness check failed: ${response.statusText}`)
    }
    return response.json()
  },

  // Phase 04: Preflight endpoints
  async triggerPreflight(request: TriggerPreflightRequest): Promise<PreflightResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/preflight/trigger`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
    if (!response.ok) {
      throw new Error(`Trigger preflight failed: ${response.statusText}`)
    }
    return response.json()
  },

  async getPreflight(preflightId: string): Promise<PreflightResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/preflight/${preflightId}`)
    if (!response.ok) {
      throw new Error(`Get preflight failed: ${response.statusText}`)
    }
    return response.json()
  },

  async getLatestPreflight(sourceId: string): Promise<PreflightResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/preflight/source/${sourceId}/latest`)
    if (!response.ok) {
      throw new Error(`Get latest preflight failed: ${response.statusText}`)
    }
    return response.json()
  },

  // Phase 19: Enhanced Frontend UI endpoints
  async getAuditSummary(): Promise<AuditSummaryStats> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/summary`)
    if (!response.ok) {
      throw new Error(`Get audit summary failed: ${response.statusText}`)
    }
    return response.json()
  },

  async getRecentAudits(limit: number = 10): Promise<RecentAudit[]> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/recent?limit=${limit}`)
    if (!response.ok) {
      throw new Error(`Get recent audits failed: ${response.statusText}`)
    }
    return response.json()
  },

  async getAuditDetails(auditRunId: string): Promise<AuditDetailsResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/${auditRunId}`)
    if (!response.ok) {
      throw new Error(`Get audit details failed: ${response.statusText}`)
    }
    return response.json()
  },

  async listFindings(filters: FindingsFilters = {}): Promise<FindingsResponse> {
    const params = new URLSearchParams()
    
    if (filters.audit_run_id) params.append('audit_run_id', filters.audit_run_id)
    if (filters.project_id) params.append('project_id', filters.project_id)
    if (filters.severity) params.append('severity', filters.severity)
    if (filters.category) params.append('category', filters.category)
    if (filters.track) params.append('track', filters.track)
    if (filters.status) params.append('status', filters.status)
    if (filters.confidence) params.append('confidence', filters.confidence)
    if (filters.search) params.append('search', filters.search)
    if (filters.sort) params.append('sort', filters.sort)
    if (filters.order) params.append('order', filters.order)
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.page_size) params.append('page_size', filters.page_size.toString())

    const url = `${API_BASE_URL}/api/v1/audit/findings/list${params.toString() ? '?' + params.toString() : ''}`
    const response = await apiFetch(url)
    
    if (!response.ok) {
      throw new Error(`List findings failed: ${response.statusText}`)
    }
    return response.json()
  },

  async getFindingDetails(findingId: string): Promise<FindingDetailsResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/finding/${findingId}`)
    if (!response.ok) {
      throw new Error(`Get finding details failed: ${response.statusText}`)
    }
    return response.json()
  },

  async getRelatedFindings(findingId: string, limit: number = 10): Promise<RelatedFindingsResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/finding/${findingId}/related?limit=${limit}`)
    if (!response.ok) {
      throw new Error(`Get related findings failed: ${response.statusText}`)
    }
    return response.json()
  },

  // Phase 4: Projects & Audit Runs endpoints
  async listProjects(): Promise<ProjectsResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/projects/list`)
    if (!response.ok) {
      throw new Error(`List projects failed: ${response.statusText}`)
    }
    return response.json()
  },

  async deleteProject(projectId: string, confirmName: string): Promise<{ ok: boolean; audit_runs_removed?: number }> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/projects/${projectId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm_name: confirmName }),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      const detail = (data as { detail?: string }).detail
      throw new Error(typeof detail === 'string' ? detail : `Delete project failed: ${response.statusText}`)
    }
    return response.json()
  },

  async deleteAuditRun(auditRunId: string): Promise<{ ok: boolean }> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/runs/${auditRunId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm: true }),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      const detail = (data as { detail?: string }).detail
      throw new Error(typeof detail === 'string' ? detail : `Delete audit failed: ${response.statusText}`)
    }
    return response.json()
  },

  async listAuditRuns(filters: AuditRunsFilters = {}): Promise<AuditRunsResponse> {
    const params = new URLSearchParams()
    
    if (filters.project_id) params.append('project_id', filters.project_id)
    if (filters.status) params.append('status', filters.status)
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.page_size) params.append('page_size', filters.page_size.toString())

    const url = `${API_BASE_URL}/api/v1/audit/runs/list${params.toString() ? '?' + params.toString() : ''}`
    const response = await apiFetch(url)
    
    if (!response.ok) {
      throw new Error(`List audit runs failed: ${response.statusText}`)
    }
    return response.json()
  },

  // Phase 7: Scoring endpoints
  async getAuditScores(auditRunId: string): Promise<ScoringResult> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/${auditRunId}/scores`)
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Audit run not found')
      } else if (response.status === 400) {
        const data = await response.json()
        throw new Error(data.detail || 'Audit run cannot be scored')
      }
      throw new Error(`Get audit scores failed: ${response.statusText}`)
    }
    return response.json()
  },

  // Phase 7: Report endpoints
  async generateReport(auditRunId: string, request: ReportGenerationRequest): Promise<ReportGenerationResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/report/generate/${auditRunId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
    if (!response.ok) {
      throw new Error(`Generate report failed: ${response.statusText}`)
    }
    return response.json()
  },

  async getReportStatus(reportId: string): Promise<ReportStatusResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/report/${reportId}/status`)
    if (!response.ok) {
      throw new Error(`Get report status failed: ${response.statusText}`)
    }
    return response.json()
  },

  async downloadReport(reportId: string): Promise<Blob> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/report/${reportId}/download`)
    if (!response.ok) {
      if (response.status === 409) {
        throw new Error('Report is not ready for download yet')
      }
      throw new Error(`Download report failed: ${response.statusText}`)
    }
    return response.blob()
  },

  async listReportsForAudit(auditRunId: string, format?: ReportFormat): Promise<ReportStatusResponse[]> {
    const params = new URLSearchParams()
    if (format) params.append('format', format)
    
    const url = `${API_BASE_URL}/api/v1/report/audit/${auditRunId}/reports${params.toString() ? '?' + params.toString() : ''}`
    const response = await apiFetch(url)
    
    if (!response.ok) {
      throw new Error(`List reports failed: ${response.statusText}`)
    }
    return response.json()
  },

  // Phase 8: Fix with AI endpoints
  async createFixRun(auditRunId: string, request: FixRunCreateRequest): Promise<FixRunCreateResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/${auditRunId}/fix`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
    
    if (!response.ok) {
      if (response.status === 503) {
        throw new Error('HMVM AI service is not available')
      } else if (response.status === 404) {
        throw new Error('Audit run not found')
      } else if (response.status === 400) {
        const data = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(data.detail || 'Invalid fix request')
      } else if (response.status === 422) {
        const data = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(data.detail || 'Validation error')
      }
      throw new Error(`Create fix run failed: ${response.statusText}`)
    }
    
    return response.json()
  },

  async getFixRunStatus(fixRunId: string): Promise<FixRunStatusResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/fix/${fixRunId}`)
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Fix run not found')
      } else if (response.status === 400) {
        throw new Error('Invalid fix run ID')
      }
      throw new Error(`Get fix run status failed: ${response.statusText}`)
    }
    
    return response.json()
  },

  async cancelFixRun(fixRunId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/fix/${fixRunId}/cancel`, {
      method: 'POST',
    })
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Fix run not found')
      } else if (response.status === 400) {
        const data = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(data.message || data.detail || 'Cannot cancel fix run')
      }
      throw new Error(`Cancel fix run failed: ${response.statusText}`)
    }
  },

  // Phase 9: FixRun History & Batch Operations endpoints
  async listFixRuns(
    auditRunId: string,
    options?: {
      statuses?: string
      search?: string
      limit?: number
      cursor?: string
    }
  ): Promise<FixRunListResponse> {
    const params = new URLSearchParams()
    if (options?.statuses) params.append('statuses', options.statuses)
    if (options?.search) params.append('search', options.search)
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.cursor) params.append('cursor', options.cursor)
    
    const url = `${API_BASE_URL}/api/v1/audit/${auditRunId}/fixruns${params.toString() ? '?' + params.toString() : ''}`
    const response = await apiFetch(url)
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Audit run not found')
      } else if (response.status === 400) {
        throw new Error('Invalid audit run ID format')
      } else if (response.status === 422) {
        const data = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(data.detail || 'Validation error')
      }
      throw new Error(`List fix runs failed: ${response.statusText}`)
    }
    
    return response.json()
  },

  async batchCancelFixRuns(request: BatchCancelRequest): Promise<BatchCancelResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/fixruns/batch-cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
    
    if (!response.ok) {
      if (response.status === 400) {
        const data = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(data.detail || 'Invalid batch cancel request')
      } else if (response.status === 422) {
        const data = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(data.detail || 'Validation error')
      }
      throw new Error(`Batch cancel failed: ${response.statusText}`)
    }
    
    return response.json()
  },

  // Phase 10: Knowledge Base endpoints
  async listKBEntries(options?: {
    search?: string
    category?: string
    severity?: string
    format_type?: string
    page?: number
    page_size?: number
  }): Promise<KBListResponse> {
    const params = new URLSearchParams()
    if (options?.search) params.append('search', options.search)
    if (options?.category) params.append('category', options.category)
    if (options?.severity) params.append('severity', options.severity)
    if (options?.format_type) params.append('format_type', options.format_type)
    if (options?.page) params.append('page', options.page.toString())
    if (options?.page_size) params.append('page_size', options.page_size.toString())
    
    const url = `${API_BASE_URL}/api/v1/knowledge-base/entries${params.toString() ? '?' + params.toString() : ''}`
    const response = await apiFetch(url)
    
    if (!response.ok) {
      throw new Error(`List KB entries failed: ${response.statusText}`)
    }
    
    return response.json()
  },

  async getKBEntry(entryId: string): Promise<KBEntryDetail> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/knowledge-base/entries/${entryId}`)
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Knowledge Base entry not found')
      }
      throw new Error(`Get KB entry failed: ${response.statusText}`)
    }
    
    return response.json()
  },

  async getKBEntryByRuleId(ruleId: string, version?: string): Promise<KBEntryDetail> {
    const params = new URLSearchParams()
    if (version) params.append('version', version)
    
    const url = `${API_BASE_URL}/api/v1/knowledge-base/entries/by-rule-id/${encodeURIComponent(ruleId)}${params.toString() ? '?' + params.toString() : ''}`
    const response = await apiFetch(url)
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Knowledge Base entry not found')
      }
      throw new Error(`Get KB entry by rule ID failed: ${response.statusText}`)
    }
    
    return response.json()
  },

  async getKBStats(): Promise<KBStatsResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/knowledge-base/stats`)
    
    if (!response.ok) {
      throw new Error(`Get KB stats failed: ${response.statusText}`)
    }
    
    return response.json()
  },

  // Phase 11: Knowledge Control (admin/governance)
  _knowledgeControlHeaders(extra?: Record<string, string>): HeadersInit {
    const token = loadKnowledgeAdminToken()
    const headers: Record<string, string> = { ...extra }
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    return headers
  },

  async _knowledgeControlFetch(path: string, init?: RequestInit): Promise<Response> {
    const response = await apiFetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        ...this._knowledgeControlHeaders(
          init?.headers as Record<string, string> | undefined
        ),
      },
    })
    if (response.status === 401 || response.status === 403) {
      throw new KnowledgeControlAuthError(
        response.status === 403 ? 'Access denied' : 'Authentication required',
        response.status
      )
    }
    return response
  },

  async listKnowledgeControlRules(options?: {
    status?: string
    format_type?: string
    category?: string
    severity?: string
    search_query?: string
    page?: number
    page_size?: number
  }): Promise<KnowledgeControlListResponse> {
    const params = new URLSearchParams()
    if (options?.status) params.append('status', options.status)
    if (options?.format_type) params.append('format_type', options.format_type)
    if (options?.category) params.append('category', options.category)
    if (options?.severity) params.append('severity', options.severity)
    if (options?.search_query) params.append('search_query', options.search_query)
    if (options?.page) params.append('page', options.page.toString())
    if (options?.page_size) params.append('page_size', options.page_size.toString())
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const response = await this._knowledgeControlFetch(`/api/v1/knowledge/rules${suffix}`)
    if (!response.ok) {
      throw new Error(`List Knowledge Control rules failed: ${response.statusText}`)
    }
    return response.json()
  },

  async createKnowledgeControlRule(body: {
    rule_id: string
    version?: string
    format_type: string
    category: string
    severity: string
    confidence: string
    title: string
    description: string
    impact?: string
    match_spec: Record<string, unknown>
    message: string
    remediation?: string
    references?: string[]
    cwe_ids?: string[]
    tags?: string[]
    provenance_source: string
    provenance_author?: string
    proposal_source?: string
    proposal_rationale: string
  }): Promise<{
    rule_id: string
    rule_identifier: string
    status: KnowledgeLifecycleStatus
    proposal_source: string
    validation_status: KnowledgeValidationStatus
    created_at: string
    message: string
  }> {
    const response = await this._knowledgeControlFetch('/api/v1/knowledge/rules', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Create rule failed')
    }
    return response.json()
  },

  async getKnowledgeControlRule(ruleId: string): Promise<KnowledgeControlDetail> {
    const response = await this._knowledgeControlFetch(`/api/v1/knowledge/rules/${ruleId}`)
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Knowledge Control rule not found')
      }
      throw new Error(`Get Knowledge Control rule failed: ${response.statusText}`)
    }
    return response.json()
  },

  async getKnowledgeControlHistory(ruleId: string): Promise<KnowledgeControlHistoryResponse> {
    const response = await this._knowledgeControlFetch(
      `/api/v1/knowledge/rules/${ruleId}/history`
    )
    if (!response.ok) {
      throw new Error(`Get Knowledge Control history failed: ${response.statusText}`)
    }
    return response.json()
  },

  async getKnowledgeControlStats(): Promise<KnowledgeControlStats> {
    const response = await this._knowledgeControlFetch('/api/v1/knowledge/stats')
    if (!response.ok) {
      throw new Error(`Get Knowledge Control stats failed: ${response.statusText}`)
    }
    return response.json()
  },

  async transitionKnowledgeControlRule(
    ruleId: string,
    body: {
      to_status: KnowledgeLifecycleStatus
      reason: string
      notes?: string
      from_status?: KnowledgeLifecycleStatus
      version_number?: number
    }
  ): Promise<KnowledgeTransitionResponse> {
    const response = await this._knowledgeControlFetch(
      `/api/v1/knowledge/rules/${ruleId}/transition`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    )
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Transition failed')
    }
    return response.json()
  },

  async approveKnowledgeControlRule(
    ruleId: string,
    body: {
      action: KnowledgeApprovalAction
      notes?: string
      revision_requested?: string
    }
  ): Promise<KnowledgeApprovalResponse> {
    const response = await this._knowledgeControlFetch(
      `/api/v1/knowledge/rules/${ruleId}/approve`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    )
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Approval failed')
    }
    return response.json()
  },

  async validateKnowledgeControlRule(
    ruleId: string,
    body?: {
      validation_types?: string[]
      positive_fixtures?: string[]
      negative_fixtures?: string[]
    }
  ): Promise<KnowledgeValidationResponse> {
    const response = await this._knowledgeControlFetch(
      `/api/v1/knowledge/rules/${ruleId}/validate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      }
    )
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Validation failed')
    }
    return response.json()
  },

  async listKnowledgePacks(options?: {
    status?: string
    format_type?: string
    search_query?: string
    tag?: string
    page?: number
    page_size?: number
  }): Promise<KnowledgePackListResponse> {
    const params = new URLSearchParams()
    if (options?.status) params.append('status', options.status)
    if (options?.format_type) params.append('format_type', options.format_type)
    if (options?.search_query) params.append('search_query', options.search_query)
    if (options?.tag) params.append('tag', options.tag)
    if (options?.page) params.append('page', options.page.toString())
    if (options?.page_size) params.append('page_size', options.page_size.toString())
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const response = await this._knowledgeControlFetch(`/api/v1/knowledge/packs${suffix}`)
    if (!response.ok) {
      throw new Error(`List Knowledge Packs failed: ${response.statusText}`)
    }
    return response.json()
  },

  async getKnowledgePack(packId: string): Promise<KnowledgePackDetail> {
    const response = await this._knowledgeControlFetch(`/api/v1/knowledge/packs/${packId}`)
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Knowledge Pack not found')
      }
      throw new Error(`Get Knowledge Pack failed: ${response.statusText}`)
    }
    return response.json()
  },

  async getKnowledgePackHistory(packId: string): Promise<KnowledgePackHistoryResponse> {
    const response = await this._knowledgeControlFetch(`/api/v1/knowledge/packs/${packId}/history`)
    if (!response.ok) {
      throw new Error(`Get Knowledge Pack history failed: ${response.statusText}`)
    }
    return response.json()
  },

  async createKnowledgePack(body: {
    pack_id: string
    version: string
    name: string
    description?: string
    format_type: string
    provenance_source?: string
    member_rule_ids?: string[]
  }): Promise<KnowledgePackDetail> {
    const response = await this._knowledgeControlFetch('/api/v1/knowledge/packs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Create pack failed')
    }
    return response.json()
  },

  async updateKnowledgePack(
    packId: string,
    body: { name?: string; description?: string; version_number?: number }
  ): Promise<KnowledgePackDetail> {
    const response = await this._knowledgeControlFetch(`/api/v1/knowledge/packs/${packId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Update pack failed')
    }
    return response.json()
  },

  async addKnowledgePackMember(packId: string, ruleId: string): Promise<KnowledgePackDetail> {
    const response = await this._knowledgeControlFetch(`/api/v1/knowledge/packs/${packId}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rule_id: ruleId }),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Add member failed')
    }
    return response.json()
  },

  async removeKnowledgePackMember(packId: string, ruleId: string): Promise<KnowledgePackDetail> {
    const response = await this._knowledgeControlFetch(
      `/api/v1/knowledge/packs/${packId}/members/${ruleId}`,
      { method: 'DELETE' }
    )
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Remove member failed')
    }
    return response.json()
  },

  async createKnowledgePackVersion(
    packId: string,
    body: { version: string; reason: string }
  ): Promise<KnowledgePackDetail> {
    const response = await this._knowledgeControlFetch(`/api/v1/knowledge/packs/${packId}/versions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Create pack version failed')
    }
    return response.json()
  },

  async transitionKnowledgePack(
    packId: string,
    body: {
      to_status: KnowledgeLifecycleStatus
      reason: string
      notes?: string
      from_status?: KnowledgeLifecycleStatus
      version_number?: number
    }
  ): Promise<KnowledgePackTransitionResponse> {
    const response = await this._knowledgeControlFetch(
      `/api/v1/knowledge/packs/${packId}/transition`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    )
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Pack transition failed')
    }
    return response.json()
  },

  async approveKnowledgePack(
    packId: string,
    body: { action: KnowledgeApprovalAction; notes?: string; revision_requested?: string }
  ): Promise<{ new_status: KnowledgeLifecycleStatus; message: string }> {
    const response = await this._knowledgeControlFetch(`/api/v1/knowledge/packs/${packId}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Pack approval failed')
    }
    return response.json()
  },

  async validateKnowledgePack(packId: string): Promise<KnowledgePackValidationResponse> {
    const response = await this._knowledgeControlFetch(`/api/v1/knowledge/packs/${packId}/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Pack validation failed')
    }
    return response.json()
  },

  async resolveKnowledgePacks(body?: {
    packs?: Array<{ pack_id: string; version?: string }>
    audit_run_id?: string
  }): Promise<KnowledgePackResolveResponse> {
    const response = await this._knowledgeControlFetch('/api/v1/knowledge/packs/resolve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(typeof data.detail === 'string' ? data.detail : 'Pack resolve failed')
    }
    return response.json()
  },

  async listKBPacks(options?: {
    search?: string
    format_type?: string
    page?: number
    page_size?: number
  }): Promise<KBPackListResponse> {
    const params = new URLSearchParams()
    if (options?.search) params.append('search', options.search)
    if (options?.format_type) params.append('format_type', options.format_type)
    if (options?.page) params.append('page', options.page.toString())
    if (options?.page_size) params.append('page_size', options.page_size.toString())
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const response = await apiFetch(`${API_BASE_URL}/api/v1/knowledge-base/packs${suffix}`)
    if (!response.ok) {
      throw new Error(`List Knowledge Base packs failed: ${response.statusText}`)
    }
    return response.json()
  },

  async startAuditRun(request: StartAuditRequest): Promise<StartAuditResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/runs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })
    if (!response.ok) {
      throw new Error(await readApiError(response, 'Failed to start audit'))
    }
    return response.json()
  },

  async listEvidence(filters: {
    project_id?: string
    audit_run_id?: string
    finding_id?: string
    search?: string
    page?: number
    page_size?: number
  } = {}): Promise<EvidenceListResponse> {
    const params = new URLSearchParams()
    if (filters.project_id) params.append('project_id', filters.project_id)
    if (filters.audit_run_id) params.append('audit_run_id', filters.audit_run_id)
    if (filters.finding_id) params.append('finding_id', filters.finding_id)
    if (filters.search) params.append('search', filters.search)
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.page_size) params.append('page_size', filters.page_size.toString())
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/evidence/list${suffix}`)
    if (!response.ok) {
      throw new Error(await readApiError(response, 'Failed to list evidence'))
    }
    return response.json()
  },

  async getRuntime(): Promise<RuntimeResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/runtime`)
    if (!response.ok) {
      throw new Error(`Get runtime failed: ${response.statusText}`)
    }
    return response.json()
  },

  async getAuditProgress(auditRunId: string): Promise<AuditProgressResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/audit/${auditRunId}/progress`)
    if (!response.ok) {
      throw new Error(await readApiError(response, 'Failed to load audit progress'))
    }
    return response.json()
  },

  async listRecentReports(limit = 50): Promise<ReportStatusResponse[]> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/report/recent?limit=${limit}`)
    if (!response.ok) {
      throw new Error(await readApiError(response, 'Failed to list reports'))
    }
    return response.json()
  },

  async listRecentFixRuns(limit = 50): Promise<{ items: RecentFixRunItem[]; total: number }> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/fixruns?limit=${limit}`)
    if (!response.ok) {
      throw new Error(await readApiError(response, 'Failed to list fix runs'))
    }
    return response.json()
  },

  async listReaudits(options?: {
    original_audit_run_id?: string
    limit?: number
    offset?: number
  }): Promise<ReauditListResponse> {
    const params = new URLSearchParams()
    if (options?.original_audit_run_id) params.append('original_audit_run_id', options.original_audit_run_id)
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.offset) params.append('offset', options.offset.toString())
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const response = await apiFetch(`${API_BASE_URL}/api/v1/reaudit/runs${suffix}`)
    if (!response.ok) {
      throw new Error(await readApiError(response, 'Failed to list re-audits'))
    }
    return response.json()
  },

  async triggerReauditFromAudit(body: {
    original_audit_run_id: string
    quick_scan?: boolean
    focus_finding_ids?: string[]
  }): Promise<ReauditTriggerResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/reaudit/from-audit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      throw new Error(await readApiError(response, 'Failed to start re-audit'))
    }
    return response.json()
  },

  async triggerReauditFromFix(body: {
    fix_run_id: string
    quick_scan?: boolean
    focus_finding_ids?: string[]
  }): Promise<ReauditTriggerResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/reaudit/trigger`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      throw new Error(await readApiError(response, 'Failed to start re-audit'))
    }
    return response.json()
  },

  async createGitIntake(body: {
    repo_url: string
    display_name: string
    ref?: string
    description?: string
  }): Promise<IntakeResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/intake/git`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      throw new Error(await readApiError(response, 'Git intake failed'))
    }
    return response.json()
  },

  async createZipIntake(file: File, display_name: string, description?: string): Promise<IntakeResponse> {
    const form = new FormData()
    form.append('file', file)
    form.append('display_name', display_name)
    if (description) form.append('description', description)
    const response = await apiFetch(`${API_BASE_URL}/api/v1/intake/zip`, {
      method: 'POST',
      body: form,
    })
    if (!response.ok) {
      throw new Error(await readApiError(response, 'ZIP intake failed'))
    }
    return response.json()
  },

  async createStagingIntake(body: {
    staging_url: string
    display_name: string
    description?: string
  }): Promise<IntakeResponse> {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/intake/staging`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      throw new Error(await readApiError(response, 'Staging intake failed'))
    }
    return response.json()
  },
}
